/**
 * Fuseau horaire applicatif — Madagascar (UTC+3 / Indian/Antananarivo).
 * Aligné sur backend/utils/appTimezone.js
 */
export const APP_TZ_IANA =
  process.env.REACT_APP_DISPLAY_TZ ||
  process.env.REACT_APP_APP_TZ ||
  'Indian/Antananarivo';

export const APP_TZ_OFFSET = '+03:00';
export const APP_LOCALE = 'fr-FR';

const PARTS_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  timeZone: APP_TZ_IANA,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false
});

function partValue(parts, type) {
  return parts.find((p) => p.type === type)?.value ?? '';
}

export function getPartsInAppTz(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const parts = PARTS_FORMATTER.formatToParts(d);
  return {
    year: partValue(parts, 'year'),
    month: partValue(parts, 'month'),
    day: partValue(parts, 'day'),
    hour: partValue(parts, 'hour'),
    minute: partValue(parts, 'minute'),
    second: partValue(parts, 'second')
  };
}

export function todayYmd(date = new Date()) {
  const p = getPartsInAppTz(date);
  if (!p) return '';
  return `${p.year}-${p.month}-${p.day}`;
}

export function addDaysYmd(ymd, deltaDays) {
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return todayYmd();
  const anchor = new Date(Date.UTC(y, m - 1, d + deltaDays, 12, 0, 0));
  return todayYmd(anchor);
}

export function daysAgoYmd(days, anchor = new Date()) {
  return addDaysYmd(todayYmd(anchor), -Math.max(0, Number(days) || 0));
}

export function dayBoundsYmd(ymd = todayYmd()) {
  const day = String(ymd).slice(0, 10);
  return {
    from: `${day} 00:00:00`,
    to: `${day} 23:59:59`
  };
}

export function parseToInstant(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  const m = s.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?)?(Z|[+-]\d{2}:\d{2})?$/
  );
  if (!m) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const [, y, mo, d, h = '0', mi = '0', sec = '0', tz] = m;
  if (tz) return new Date(s);
  const utcMs = Date.UTC(+y, +mo - 1, +d, +h - 3, +mi, +sec);
  return new Date(utcMs);
}

export function formatDateTimeForDb(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const naive = trimmed.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (naive && !/(Z|[+-]\d{2}:?\d{2})$/i.test(trimmed)) {
      return `${naive[1]} ${naive[2]}:${naive[3]}:${naive[4] || '00'}`;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return `${trimmed} 00:00:00`;
    const parsed = parseToInstant(trimmed);
    if (parsed) {
      const p = getPartsInAppTz(parsed);
      return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second}`;
    }
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const p = getPartsInAppTz(value);
    return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second}`;
  }
  return null;
}

export function formatDateForDb(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const m = String(value).trim().match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const p = getPartsInAppTz(value);
    return `${p.year}-${p.month}-${p.day}`;
  }
  return null;
}

/** Valeur pour input datetime-local (horloge MG). */
export function toDateTimeLocalValue(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const s = value.trim();
    if (/(Z|[+-]\d{2}:?\d{2})$/i.test(s)) {
      const instant = parseToInstant(s);
      if (instant) {
        const p = getPartsInAppTz(instant);
        return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
      }
    }
    const m = s.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
    if (m) return `${m[1]}T${m[2]}`;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const p = getPartsInAppTz(value);
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
  }
  return '';
}

export function fromDateTimeLocalValue(value) {
  return formatDateTimeForDb(value);
}

export function nowDateTimeLocalValue() {
  return toDateTimeLocalValue(new Date());
}

export function combineYmdAndTime(ymd, hhmm) {
  if (!ymd || !hhmm) return null;
  const [h, mi] = String(hhmm).split(':');
  return `${String(ymd).slice(0, 10)} ${h}:${mi || '00'}:00`;
}

export function formatAppDateTime(value, options = {}) {
  const instant = parseToInstant(value);
  if (!instant) return '—';

  const base = { timeZone: APP_TZ_IANA, ...options };
  const hasGranular = ['year', 'month', 'day', 'hour', 'minute', 'second', 'weekday'].some(
    (key) => base[key] != null
  );

  if (hasGranular) {
    delete base.dateStyle;
    delete base.timeStyle;
    return instant.toLocaleString(APP_LOCALE, base);
  }

  if (base.dateStyle == null) base.dateStyle = 'short';
  if (base.timeStyle == null) base.timeStyle = 'short';
  return instant.toLocaleString(APP_LOCALE, base);
}

export function formatAppDate(value, options = {}) {
  const instant = parseToInstant(value);
  if (!instant) return '—';
  return instant.toLocaleDateString(APP_LOCALE, {
    timeZone: APP_TZ_IANA,
    ...options
  });
}

export function formatAppTime(value, options = {}) {
  const instant = parseToInstant(value);
  if (!instant) return '—';
  return instant.toLocaleTimeString(APP_LOCALE, {
    timeZone: APP_TZ_IANA,
    hour: '2-digit',
    minute: '2-digit',
    ...options
  });
}

export function parseCalendarDate(value) {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function formatCalendarDate(value, options = {}) {
  const d = parseCalendarDate(value);
  if (!d) return '—';
  return d.toLocaleDateString(APP_LOCALE, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...options
  });
}

/** @deprecated alias — préférer todayYmd */
export function isoDateInAppTz(date = new Date()) {
  return todayYmd(date);
}
